import React, { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Textarea from "@cloudscape-design/components/textarea";
import Tabs from "@cloudscape-design/components/tabs";
import Toggle from "@cloudscape-design/components/toggle";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import DatePicker from "@cloudscape-design/components/date-picker";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Container from "@cloudscape-design/components/container";
import Badge from "@cloudscape-design/components/badge";
import { notificationsAddModalStore } from "../stores/notificationsAddModal";
import { notificationsEditModalStore } from "../stores/notificationsEditModal";
import { notificationTagsStore } from "../stores/notificationTags";
import { DateRangePicker } from "@cloudscape-design/components";
import Grid from "@cloudscape-design/components/grid";
import Table from "@cloudscape-design/components/table";
import { RISK_MODEL_OPTIONS, getRiskModelSelectedOptions } from "../helpers/riskModelOptions";
import { PARTNER_PAYMENT_TYPE_OPTIONS, getCohortFieldOptions, getCohortOperatorOptions } from "../helpers/cohortConditions";

const LANGUAGE_COHORT_FIELD_OPTION = getCohortFieldOptions().find((option) => option.value === "lang");
const DEFAULT_NOTIFICATION_COHORT_OPERATOR_OPTIONS = ["=", "!=", ">", "<", ">=", "<=", "IN"].map((operator) => ({
	value: operator,
	label: operator,
}));
const LANGUAGE_COHORT_OPERATOR_OPTIONS = getCohortOperatorOptions(LANGUAGE_COHORT_FIELD_OPTION);

const formatLanguageCohortValue = (value, operator) => {
	const isListOperator = ["IN", "NOT IN"].includes(operator);
	const values = (Array.isArray(value) ? value : isListOperator ? String(value ?? "").split(",") : [value])
		.map((item) => String(item ?? "").trim().toLowerCase())
		.filter(Boolean);

	return isListOperator ? values : values[0] || "";
};

function NotificationModal(props) {
	const { Interface, mode } = props;

	const addModalStore = useRecoilValue(notificationsAddModalStore);
	const setAddModalStore = useSetRecoilState(notificationsAddModalStore);
	const editModalStore = useRecoilValue(notificationsEditModalStore);
	const setEditModalStore = useSetRecoilState(notificationsEditModalStore);
	const notificationTags = useRecoilValue(notificationTagsStore);

	const store = mode === "add" ? addModalStore : editModalStore;
	const setStore = mode === "add" ? setAddModalStore : setEditModalStore;

	const [activeTab, setActiveTab] = React.useState("general");
	const [fieldErrors, setFieldErrors] = React.useState({});
	const [cohortEnabled, setCohortEnabled] = React.useState(false);
	const [buttonEnabled, setButtonEnabled] = React.useState(false);
	const [jsonText, setJsonText] = React.useState("");
	const [jsonError, setJsonError] = React.useState("");

	const [isLoading, setIsLoading] = useState(false);
	const [newCondition, setNewConditions] = useState({});

	const isConditionValueEmpty = (value) => value === null || value === "" || (Array.isArray(value) && value.length === 0);

	const createDefaultFormData = () => ({
		image: "",
		imageFile: null,
		localName: "",
		imagePreview: "",
		buttonType: "",
		buttonLink: "",
		buttonLinkType: "",
		buttonTranslationType: "",
		buttonText: "",
		linkType: mode === "add" ? "internal" : "",
		link: "",
		title: "",
		titleType: "",
		description: "",
		descriptionType: "",
		priority: 1,
		active: true,
		from: "",
		to: "",
		type: "default",
		tags: [],
		conditions: [],
		cohort: {
			dateRegistrationFrom: "",
			dateRegistrationTo: "",
			dateDepositFrom: "",
			dateDepositTo: "",
			depositCount: "",
			depositType: "",
			emailExists: null,
			emailVerify: null,
			tgExists: null,
			vipLvl: "",
			phoneExists: null,
		},
		showSettings: [
			{
				page: "",
				seconds: 5,
			},
		],
	});

	const getOptionValue = (value) => {
		if (value && typeof value === "object" && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, "value")) {
			return value.value;
		}

		return value;
	};

	const parseJsonValue = (value) => {
		if (typeof value !== "string") {
			return value;
		}

		if (!value.trim()) {
			return value;
		}

		try {
			return JSON.parse(value);
		} catch (error) {
			return value;
		}
	};

	const normalizeShowSettings = (data) => {
		const rawShowSettings = parseJsonValue(data.showSettings || data.showSettingsConditions);

		if (Array.isArray(rawShowSettings)) {
			return rawShowSettings.length ? rawShowSettings : createDefaultFormData().showSettings;
		}

		if (rawShowSettings && typeof rawShowSettings === "object") {
			return [rawShowSettings];
		}

		if (data.page || data.seconds !== undefined) {
			return [
				{
					page: data.page || "",
					seconds: data.seconds !== undefined ? Number(data.seconds) : 5,
				},
			];
		}

		return createDefaultFormData().showSettings;
	};

	const normalizeConditions = (data) => {
		const rawConditions = parseJsonValue(data.conditions || data.cohort || data.cohortConditions);

		if (Array.isArray(rawConditions)) {
			return rawConditions;
		}

		return [];
	};

	const normalizeNotificationFormData = (data) => {
		const defaultFormData = createDefaultFormData();
		const rawShowSettings = normalizeShowSettings(data);
		const showSettings = rawShowSettings.map((settings) => {
			const normalizedSettings = { ...settings };
			delete normalizedSettings.tags;
			return normalizedSettings;
		});
		let notificationTagIds = data.notificationTagIds;
		if (typeof notificationTagIds === "string") {
			try {
				notificationTagIds = JSON.parse(notificationTagIds);
			} catch (error) {
				notificationTagIds = [];
			}
		}
		const normalizedData = {
			...defaultFormData,
			...data,
			imageFile: null,
			showSettings,
			conditions: normalizeConditions(data),
			tags: (Array.isArray(data.tags) ? data.tags : notificationTagIds || []).map(Number).filter((id) => Number.isInteger(id) && id > 0),
		};

		["buttonType", "buttonLinkType", "buttonTranslationType", "linkType", "titleType", "descriptionType", "type"].forEach((key) => {
			normalizedData[key] = getOptionValue(normalizedData[key]) || defaultFormData[key] || "";
		});

		normalizedData.priority = normalizedData.priority === "" || normalizedData.priority === null || normalizedData.priority === undefined ? 1 : Number(normalizedData.priority);
		normalizedData.active = normalizedData.active === true || normalizedData.active === 1 || normalizedData.active === "1";

		if (!normalizedData.cohort || typeof normalizedData.cohort !== "object" || Array.isArray(normalizedData.cohort)) {
			normalizedData.cohort = defaultFormData.cohort;
		}

		return normalizedData;
	};

	const getSerializableFormData = (formData) => {
		const serializableData = cloneDeep(formData || createDefaultFormData());
		serializableData.imageFile = null;
		delete serializableData.notificationTagIds;

		return serializableData;
	};

	const formatJsonForEditor = (formData) => JSON.stringify(getSerializableFormData(formData), null, 2);

	const hasButtonData = (formData) => Boolean(formData.buttonType || formData.buttonLink || formData.buttonText || formData.buttonLinkType || formData.buttonTranslationType);

	const getJsonValidationError = (value) => {
		if (!value.trim()) {
			return "JSON is required";
		}

		try {
			const parsedData = JSON.parse(value);

			if (!parsedData || typeof parsedData !== "object" || Array.isArray(parsedData)) {
				return "JSON must be an object";
			}
		} catch (error) {
			return error.message ? `Invalid JSON: ${error.message}` : "Invalid JSON format";
		}

		return "";
	};

	React.useEffect(() => {
		if (store.show && activeTab !== "json") {
			setJsonText(formatJsonForEditor(store.formData));
			setJsonError("");
		}
	}, [store.show, store.formData, activeTab]);

	React.useEffect(() => {
		if (store.show && !notificationTags.loaded) {
			Interface.loadNotificationTags().catch(() => {});
		}
	}, [Interface, notificationTags.loaded, store.show]);


	const updateFormField = (key, value) => {
		const updatedData = cloneDeep(store.formData);

		if (key.includes(".")) {
			const keys = key.split(".");
			let ref = updatedData;
			for (let i = 0; i < keys.length - 1; i++) {
				if (!ref[keys[i]]) {
					ref[keys[i]] = {};
				}
				ref = ref[keys[i]];
			}
			ref[keys[keys.length - 1]] = value;
		} else {
			updatedData[key] = value;
		}

		setStore({ ...store, formData: updatedData });
	};

	const updateShowSetting = (index, field, value) => {
		const updatedData = cloneDeep(store.formData);

		if (!updatedData.showSettings) {
			updatedData.showSettings = [{ page: "", seconds: 5 }];
		}
		if (!updatedData.showSettings[index]) {
			updatedData.showSettings[index] = { page: "", seconds: 5 };
		}

		updatedData.showSettings[index][field] = value;
		setStore({ ...store, formData: updatedData });
	};

	const validateForm = (isButtonEnabled = buttonEnabled, isCohortEnabled = cohortEnabled) => {
		const formData = store.formData;
		const errors = [];
		const newFieldErrors = {};

		if (!formData.localName) {
			errors.push("Local Name");
			newFieldErrors.localName = true;
		}
		if (!formData.title) {
			errors.push("Title");
			newFieldErrors.title = true;
		}
		if (!formData.titleType) {
			errors.push("Title Type");
			newFieldErrors.titleType = true;
		}
		if (!formData.description) {
			errors.push("Description");
			newFieldErrors.description = true;
		}
		if (!formData.descriptionType) {
			errors.push("Description Type");
			newFieldErrors.descriptionType = true;
		}
		if (!formData.from) {
			errors.push("From Date");
			newFieldErrors.from = true;
		}
		if (!formData.to) {
			errors.push("To Date");
			newFieldErrors.to = true;
		}
		if (formData.priority === undefined || formData.priority === null) {
			errors.push("Priority");
			newFieldErrors.priority = true;
		}

		if (!formData.showSettings || formData.showSettings.length === 0) {
			errors.push("At least one Show Setting");
			newFieldErrors.showSettings = true;
		} else {
			if (!formData.showSettings[0].page) {
				errors.push("Page in Show Settings");
				newFieldErrors.page = true;
			}
			if (formData.showSettings[0].seconds === undefined || formData.showSettings[0].seconds === "" || formData.showSettings[0].seconds === null || isNaN(formData.showSettings[0].seconds)) {
				errors.push("Display Delay in Show Settings");
				newFieldErrors.seconds = true;
			}
		}

		if (isCohortEnabled) {
			if ((formData.cohort.depositType || formData.cohort.dateDepositFrom || formData.cohort.dateDepositTo) && !formData.cohort.depositCount && formData.cohort.depositCount < 0) {
				errors.push("Deposit Count is required when Deposit Type or Deposit Date Range is set");
				newFieldErrors["cohort.depositCount"] = true;
			}
		}

		if (isButtonEnabled) {
			if (!formData.buttonType) {
				errors.push("Button Design Type");
				newFieldErrors.buttonType = true;
			}
			if (!formData.buttonLink) {
				errors.push("Button Link");
				newFieldErrors.buttonLink = true;
			}
			if (!formData.buttonText) {
				errors.push("Button Text");
				newFieldErrors.buttonText = true;
			}
			if (!formData.buttonTranslationType) {
				errors.push("Button Translation Type");
				newFieldErrors.buttonTranslationType = true;
			}
			if (!formData.buttonLinkType) {
				errors.push("Button Link Type");
				newFieldErrors.buttonLinkType = true;
			}
		}

		if (errors.length > 0) {
			setFieldErrors(newFieldErrors);
			return { valid: false, errors };
		}

		setFieldErrors({});
		return { valid: true };
	};

	const handleSave = async () => {
		if (jsonError) {
			setActiveTab("json");
			Interface.addNoty("Error", "Please fix JSON before saving");
			return;
		}

		const validation = validateForm();
		if (!validation.valid) {
			Interface.addNoty("Error", `Please fill in the following required fields: ${validation.errors.join(", ")}`);
			return;
		}

		const formData = store.formData;

		let imageBase64 = null;

		if (formData.imageFile) {
			try {
				imageBase64 = await new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = () => resolve(reader.result);
					reader.onerror = reject;
					reader.readAsDataURL(formData.imageFile);
				});
			} catch (error) {
				Interface.addNoty("Error", "Failed to process image file");
				return;
			}
		}

		const backendData = {};

		if (mode === "edit" && store.editId) {
			backendData.id = store.editId;
		}

		if (formData.localName) backendData.localName = formData.localName;

		if (imageBase64) {
			backendData.image = imageBase64;
			backendData.imagePath = formData.imageFile.name.split(".").reverse()[0];
		} else if (formData.image) {
			backendData.image = formData.image;

			if (backendData.imagePath) {
				backendData.imagePath = formData.imageFile.name.split(".").reverse()[0];
			}
		}

		if (buttonEnabled) {
			if (formData.buttonType) backendData.buttonType = formData.buttonType;
			if (formData.buttonLink) backendData.buttonLink = formData.buttonLink;
			if (formData.buttonLinkType) backendData.buttonLinkType = formData.buttonLinkType;
			if (formData.buttonText) backendData.buttonText = formData.buttonText;
			if (formData.buttonTranslationType) backendData.buttonTranslationType = formData.buttonTranslationType;
		}

		backendData.link = formData.link || "";
		backendData.linkType = { value: formData.linkType || "internal" };

		if (formData.title) backendData.title = formData.title;
		if (formData.titleType) backendData.titleType = formData.titleType;
		if (formData.description) backendData.description = formData.description;
		if (formData.descriptionType) backendData.descriptionType = formData.descriptionType;
		if (formData.priority !== undefined) backendData.priority = formData.priority;
		if (formData.from) backendData.from = formData.from;
		if (formData.to) backendData.to = formData.to;

		if (formData.type) backendData.type = formData.type;
		backendData.tags = Array.isArray(formData.tags) ? [...new Set(formData.tags.map(Number).filter((id) => Number.isInteger(id) && id > 0))] : [];

		backendData.active = formData.active ? 1 : 0;


		if (formData.conditions) {
			backendData.cohort = formData.conditions || [];
		}

		if (formData.showSettings && formData.showSettings.length > 0) {
			if (formData.showSettings[0].page) backendData.page = formData.showSettings[0].page;
			if (formData.showSettings[0].seconds !== undefined) backendData.seconds = formData.showSettings[0].seconds;
		}


		try {
			setIsLoading(true);
			const method = mode === "edit" ? "update" : "create";
			await Interface.request(
				"set",
				{
					class: "data",
					module: "notification",
					method: method,
				},
				backendData,
			);

			Interface.addNoty("Success", `Notification ${mode === "edit" ? "updated" : "created"} successfully`);
			Interface.loadNotifications();
			if (typeof Interface.loadNotificationQueue === "function") {
				Interface.loadNotificationQueue();
			}

			handleClose();
		} catch (error) {
			console.error(`Error ${mode === "edit" ? "updating" : "creating"} notification:`, error);
			Interface.addNoty("Error", error.text || error.toString());
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setActiveTab("general");
		setFieldErrors({});
		setCohortEnabled(false);
		setButtonEnabled(false);
		setJsonText("");
		setJsonError("");
		setStore({
			...store,
			show: false,
			editId: null,
			formData: createDefaultFormData(),
		});
	};

	const cloneNotification = () => {
		const cloneData = {
			...editModalStore.formData,
			localName: (editModalStore.formData.localName || "") + "_copy",
			active: false,
			imageFile: null,
			image: null,
		};

		setEditModalStore({
			...editModalStore,
			show: false,
			editId: null,
			formData: {},
		});

		setAddModalStore({
			...addModalStore,
			show: true,
			formData: cloneData,
		});

		Interface.addNoty("Info", "Notification data copied. Please review and save to create a new notification.");
	};

	const formatDateForPicker = (dateString) => {
		if (!dateString) return "";
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return "";
		return date.toISOString().split("T")[0];
	};

	const clearFieldError = (fieldName) => {
		if (fieldErrors[fieldName]) {
			setFieldErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[fieldName];
				return newErrors;
			});
		}
	};

	const copyTextToClipboard = async (value) => {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(value);
			return;
		}

		const textarea = document.createElement("textarea");
		textarea.value = value;
		textarea.setAttribute("readonly", "");
		textarea.style.position = "absolute";
		textarea.style.left = "-9999px";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		document.body.removeChild(textarea);
	};

	const copyNotificationJson = async () => {
		const value = formatJsonForEditor(store.formData);
		setJsonText(value);
		setJsonError("");

		try {
			await copyTextToClipboard(value);
			Interface.addNoty("Success", "Notification JSON copied");
		} catch (error) {
			Interface.addNoty("Error", "Failed to copy notification JSON");
		}
	};

	const applyJsonToForm = () => {
		try {
			const validationError = getJsonValidationError(jsonText);

			if (validationError) {
				throw new Error(validationError);
			}

			const parsedData = JSON.parse(jsonText);

			const normalizedData = normalizeNotificationFormData(parsedData);
			setStore({ ...store, formData: normalizedData });
			setButtonEnabled(hasButtonData(normalizedData));
			setFieldErrors({});
			setJsonText(formatJsonForEditor(normalizedData));
			setJsonError("");
			Interface.addNoty("Success", "JSON applied to notification form");
		} catch (error) {
			const message = error.message || "Invalid JSON format";
			setJsonError(message);
			Interface.addNoty("Error", message);
		}
	};


	const renderGeneralTab = () => {
		const formData = store.formData;


		return (
			<Container>
				<SpaceBetween size="m">
					<ColumnLayout columns={2}>
						<FormField label="Local Name *" description="Internal identifier" errorText={fieldErrors.localName ? "This field is required" : ""}>
							<Input
								value={formData.localName || ""}
								onChange={(e) => {
									updateFormField("localName", e.detail.value);
									clearFieldError("localName");
								}}
								placeholder="e.g., summer_promo_2024"
								invalid={fieldErrors.localName}
							/>
						</FormField>

						<FormField label="Priority *" description="1 - lowest, 5 - highest priority" errorText={fieldErrors.priority ? "This field is required" : ""}>
							<Select
								selectedOption={formData.priority ? { value: String(formData.priority), label: String(formData.priority) } : { value: "1", label: "1" }}
								onChange={(e) => updateFormField("priority", parseInt(e.detail.selectedOption.value))}
								options={[
									{ value: "1", label: "1 - Lowest" },
									{ value: "2", label: "2 - Low" },
									{ value: "3", label: "3 - Medium" },
									{ value: "4", label: "4 - High" },
									{ value: "5", label: "5 - Highest" },
								]}
								placeholder="Select priority"
							/>
						</FormField>

						<FormField label="Title *" errorText={fieldErrors.title ? "This field is required" : ""}>
							<Input
								value={formData.title || ""}
								onChange={(e) => {
									updateFormField("title", e.detail.value);
									clearFieldError("title");
								}}
								placeholder="Notification title"
								invalid={fieldErrors.title}
							/>
						</FormField>

						<FormField label="Title Type *" errorText={fieldErrors.titleType ? "This field is required" : ""}>
							<Select
								selectedOption={formData.titleType ? { value: formData.titleType, label: formData.titleType.charAt(0).toUpperCase() + formData.titleType.slice(1) } : null}
								onChange={(e) => {
									updateFormField("titleType", e.detail.selectedOption ? e.detail.selectedOption.value : "");
									clearFieldError("titleType");
								}}
								options={[
									{ value: "local", label: "Local" },
									{ value: "international", label: "International" },
									{ value: "mixed", label: "Mixed" },
								]}
								placeholder="Select title type"
								invalid={fieldErrors.titleType}
							/>
						</FormField>

						<FormField label="Type">
							<Select
								selectedOption={formData.type ? { value: formData.type, label: formData.type.charAt(0).toUpperCase() + formData.type.slice(1) } : null}
								onChange={(e) => {
									updateFormField("type", e.detail.selectedOption ? e.detail.selectedOption.value : "");
									clearFieldError("type");
								}}
								options={[
									{ value: "default", label: "Default" },
									{ value: "system", label: "System" },
								]}
								placeholder="Select title type"
								invalid={fieldErrors.titleType}
							/>
						</FormField>

						<FormField label="Tags">
							<Multiselect
								selectedOptions={(Array.isArray(formData.tags) ? formData.tags : []).map((id) => {
									const tag = notificationTags.data.find((item) => Number(item.id) === Number(id));
									return { value: String(id), label: tag ? tag.name : `#${id}` };
								})}
								onChange={({ detail }) => updateFormField("tags", detail.selectedOptions.map((option) => Number(option.value)))}
								options={notificationTags.data.filter((tag) => !tag.system && tag.key !== "all" && tag.key !== "unread").map((tag) => ({ value: String(tag.id), label: tag.name }))}
								placeholder={notificationTags.loaded ? "Select tags" : "Loading tags"}
								disabled={!notificationTags.loaded}
							/>
						</FormField>

						<FormField label="Active">
							<Toggle checked={formData.active || false} onChange={(e) => updateFormField("active", e.detail.checked)}>
								{formData.active ? "Active" : "Inactive"}
							</Toggle>
						</FormField>
					</ColumnLayout>

					<ColumnLayout columns={1}>
						<FormField label="Description *" errorText={fieldErrors.description ? "This field is required" : ""}>
							<Textarea
								value={formData.description || ""}
								onChange={(e) => {
									updateFormField("description", e.detail.value);
									clearFieldError("description");
								}}
								placeholder="Notification description"
								rows={3}
								invalid={fieldErrors.description}
							/>
						</FormField>

						<ColumnLayout columns={2}>
							<FormField label="Description Type *" errorText={fieldErrors.descriptionType ? "This field is required" : ""}>
								<Select
									selectedOption={formData.descriptionType ? { value: formData.descriptionType, label: formData.descriptionType.charAt(0).toUpperCase() + formData.descriptionType.slice(1) } : null}
									onChange={(e) => {
										updateFormField("descriptionType", e.detail.selectedOption ? e.detail.selectedOption.value : "");
										clearFieldError("descriptionType");
									}}
									options={[
										{ value: "local", label: "Local" },
										{ value: "international", label: "International" },
										{ value: "mixed", label: "Mixed" },
									]}
									placeholder="Select description type"
									invalid={fieldErrors.descriptionType}
								/>
							</FormField>
							<FormField label="Date From/To *" errorText={fieldErrors.to || fieldErrors.from ? "This field is required" : ""}>
								<DateRangePicker
									onChange={({ detail }) => {
										setStore((prev) => ({
											...prev,
											formData: {
												...prev.formData,
												from: detail.value.startDate.split("+")[0],
												to: detail.value.endDate.split("+")[0],
											},
										}));

										clearFieldError("from");
										clearFieldError("to");
									}}
									value={{
										startDate: formatDateForPicker(formData.from) || "",
										endDate: formatDateForPicker(formData.to) || "",
										type: "absolute",
									}}
									relativeOptions={[]}
									isValidRange={(range) => {
										if (range.type === "absolute") {
											const [startDateWithoutTime] = range.startDate.split("T");
											const [endDateWithoutTime] = range.endDate.split("T");
											if (!startDateWithoutTime || !endDateWithoutTime) {
												return {
													valid: false,
													errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
												};
											}
											if (new Date(range.startDate) - new Date(range.endDate) > 0) {
												return {
													valid: false,
													errorMessage: "The selected date range is invalid. The start date must be before the end date.",
												};
											}
										} else {
											return { valid: false };
										}
									}}
									i18nStrings={{
										todayAriaLabel: "Today",
										nextMonthAriaLabel: "Next month",
										previousMonthAriaLabel: "Previous month",
										customRelativeRangeDurationLabel: "Duration",
										customRelativeRangeDurationPlaceholder: "Enter duration",
										customRelativeRangeOptionLabel: "Custom range",
										customRelativeRangeOptionDescription: "Define a custom range in the past",
										customRelativeRangeUnitLabel: "Unit of time",
										formatRelativeRange: ({ amount, unit, startDate, endDate }) => {
											const start = new Date(startDate);
											const end = new Date(endDate);

											const locale = "en-GB";
											const dateOptions = { year: "numeric", month: "long", day: "numeric" };
											// const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

											const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
											const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

											return `${formattedStart} - ${formattedEnd}`;
										},
										formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
										dateTimeConstraintText: "Date and time must be within the selected range.",
										renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
										labelRelativeRange: "Relative range",
										labelAbsoluteRange: "Absolute range",
										dateTimePickerAriaLabel: "Date and time picker",
										dateTimePickerLabel: "Date and Time",
										clearButtonAriaLabel: "Clear date range",
										relativeModeTitle: "Relative mode",
										absoluteModeTitle: "Absolute mode",
										cancelButtonLabel: "Cancel",
										applyButtonLabel: "Apply",
									}}
									dateOnly
									placeholder="From/to"
								/>
							</FormField>
						</ColumnLayout>
					</ColumnLayout>

					<ColumnLayout columns={2}>
						<FormField label="Upload Image" constraintText="Optional. JPG/PNG format only. Will be uploaded to server.">
							<div
								style={{
									border: "1px dashed #d1d5db",
									borderRadius: "8px",
									padding: "16px",
									textAlign: "center",
									backgroundColor: "#fafafa",
								}}>
								<input
									type="file"
									accept="image/jpeg,image/jpg,.jpg,.jpeg,.JPG,.JPEG,image/png"
									onChange={(e) => {
										const file = e.target.files[0];
										if (file) {
											updateFormField("imageFile", file);
										} else {
											updateFormField("imageFile", null);
										}
									}}
									style={{ marginBottom: "8px" }}
								/>
								{(formData.imageFile || formData.image) && (
									<>
										<div style={{ marginTop: "8px" }}>
											<img width={200} src={formData.imageFile ? URL.createObjectURL(formData.imageFile) : formData.image} />
										</div>
									</>
								)}
							</div>
						</FormField>

						<FormField label="Image Link" description="Optional. Makes the image clickable in the opened notification">
							<Input value={formData.link || ""} onChange={(e) => updateFormField("link", e.detail.value)} placeholder="https://example.com or /page/path" />
						</FormField>

						<FormField label="Image Link Type">
							<Select
								selectedOption={formData.linkType ? { value: formData.linkType, label: formData.linkType.charAt(0).toUpperCase() + formData.linkType.slice(1) } : { value: "internal", label: "Internal" }}
								onChange={(e) => updateFormField("linkType", e.detail.selectedOption ? e.detail.selectedOption.value : "internal")}
								options={[
									{ value: "internal", label: "Internal" },
									{ value: "external", label: "External" },
									{ value: "modal", label: "Modal" },
								]}
								placeholder="Select link type"
							/>
						</FormField>
					</ColumnLayout>
				</SpaceBetween>
			</Container>
		);
	};

	const renderCohortTab = () => {
		const formData = store.formData;
		const cohort = formData.conditions || [];
		const cohortOperatorOptions = newCondition?.field === "lang" ? LANGUAGE_COHORT_OPERATOR_OPTIONS : DEFAULT_NOTIFICATION_COHORT_OPERATOR_OPTIONS;


		function renderComponentFromType(type, specialType) {
			//count boolean date string

			if (type === "multiselect" && specialType === "riskModel") {
				return <Multiselect
					selectedOptions={getRiskModelSelectedOptions(newCondition?.value)}
					onChange={({ detail }) => setNewConditions({ ...newCondition, value: detail.selectedOptions.map((option) => option.value) })}
					options={RISK_MODEL_OPTIONS}
					placeholder="Select risk models"
					selectedAriaLabel="Selected"
				/>
			} else if (type === "string") {
				return <Input
					placeholder="Enter value"
					value={newCondition?.value || ""}
					onChange={(e) =>
						setNewConditions({ ...newCondition, value: e.detail.value })
					}
				/>
			} else if (type === "count") {
				return <Input
					placeholder="Enter value"
					type="number"
					value={newCondition?.value || ""}
					onChange={(e) =>
						setNewConditions({ ...newCondition, value: e.detail.value })
					}
				/>
			} else if (type === "date") {
				return <DatePicker
					onChange={({ detail }) => {
						setNewConditions({ ...newCondition, value: detail.value });
					}}
					value={newCondition?.value|| null}
					placeholder="Select a date"
				/>
			} else if (type === "select" && specialType === "boolean") {
				return <Select
					selectedOption={
						newCondition?.value
					}
					onChange={({ detail }) =>
						setNewConditions({ ...newCondition, value: detail.selectedOption })
					}
					options={[{ value: "1", label: "Required" }, { value: "0", label: "No" }]}
					placeholder="Select type"
				/>
			} else if (type === "select" && specialType === "cryptoOrFiat") {
				return <Select
					selectedOption={
						newCondition?.value
					}
					onChange={({ detail }) =>
						setNewConditions({ ...newCondition, value: detail.selectedOption })
					}
					options={[{ value: "crypto", label: "Crypto" }, { value: "fiat", label: "Fiat" }]}
					placeholder="Select type"
				/>
			} else if (type === "select" && specialType === "partnerPaymentType") {
				return <Select
					selectedOption={
						newCondition?.value
					}
					onChange={({ detail }) =>
						setNewConditions({ ...newCondition, value: detail.selectedOption })
					}
					options={PARTNER_PAYMENT_TYPE_OPTIONS}
					placeholder="Select payment type"
				/>
			}

			return null;
		}


		return (
			<Container>
				<SpaceBetween size="m">
					<Box variant="h3">User Cohort Settings</Box>
					<>
						<Table
							columnDefinitions={[
								{ id: "field", header: "Field", cell: (item) => item.fieldLabel || item.field  },
								{ id: "operator", header: "Operator", cell: (item) => item.operator },
								{ id: "value", header: "Value", cell: (item) => Array.isArray(item.value) ? item.value.join(", ") : item.value },
								{
									id: "actions",
									header: "Control",
									cell: (item) => (
										<Button
											variant="icon"
											iconName="delete-marker"
											onClick={() => {
												const newConds = [...cohort.filter(el => el.id !== item.id)];
												updateFormField("conditions", newConds);
											}}
										>
											Remove
										</Button>
									),
								},
							]}
							footer={<>
								<FormField
									stretch
									label="Conditions"
									description="Conditions for showing the achievement"
								>
									<Grid gridDefinition={[{ colspan: 4 },{ colspan: 3 },{ colspan: 3 },{ colspan: 2 }]}>
										<Select
											selectedOption={newCondition?.field ? { value: newCondition?.field, label: newCondition?.fieldLabel }  : null}
											placeholder="Select field"
											options={[
												{ value: "id", label: "Id", type: "string" },
												{ value: "risk_model", label: "Risk model", type: "multiselect", specialType: "riskModel" },
												{ value: "bot_id", label: "Bot id", type: "string" },
												{ value: "verify", label: "Verify", type: "select", specialType: "boolean" },
												{ value: "email", label: "Email", type: "select", specialType: "boolean" },
												{ value: "phone", label: "Phone", type: "select", specialType: "boolean" },
												{ value: "tg", label: "Telegram", type: "select", specialType: "boolean" },
												LANGUAGE_COHORT_FIELD_OPTION,
												{ value: "partnerName", label: "Partner Name", type: "string" },
												{ value: "paymentType", label: "Payment Type", type: "select", specialType: "partnerPaymentType" },
												{ value: "trafficType", label: "Traffic Type", type: "string" },
												{ value: "tasks_count", label: "Tasks count", type: "count" },
												{ value: "tasks_days", label: "Tasks days", type: "count" },
												{ value: "deposit_date", label: "Deposit date", type: "count" },
												{ value: "deposit_date_crypto", label: "Deposit crypto date", type: "count" },
												{ value: "deposit_date_fiat", label: "Deposit fiat date", type: "count" },
												{ value: "deposit_count", label: "Deposit count", type: "count" },
												{ value: "deposit_count_fiat", label: "Deposit fiat count", type: "count" },
												{ value: "deposit_count_crypto", label: "Deposit crypto count", type: "count" },
												{ value: "deposit_from", label: "Deposit from", type: "date" },
												{ value: "deposit_to", label: "Deposit to", type: "date" },
												{ value: "deposit_crypto_from", label: "Deposit crypto from", type: "date" },
												{ value: "deposit_crypto_to", label: "Deposit crypto to", type: "date" },
												{ value: "deposit_fiat_from", label: "Deposit fiat from", type: "date" },
												{ value: "deposit_fiat_to", label: "Deposit fiat to", type: "date" },
												{ value: "deposit_amount_to", label: "Deposit amount to", type: "count" },
												{ value: "deposit_amount_from", label: "Deposit amount from", type: "count" },
												{ value: "deposit_amount_crypto_to", label: "Deposit amount crypto to", type: "count" },
												{ value: "deposit_amount_crypto_from", label: "Deposit amount crypto from", type: "count" },
												{ value: "deposit_amount_fiat_to", label: "Deposit amount fiat to", type: "count" },
												{ value: "deposit_amount_fiat_from", label: "Deposit amount fiat from", type: "count" },
												{ value: "deposit_type", label: "Deposit type", type: "select", specialType: "cryptoOrFiat" },
												{ value: "withdrawal_date", label: "Withdrawal date", type: "count" },
												{ value: "withdrawal_date_crypto", label: "Withdrawal crypto date", type: "count" },
												{ value: "withdrawal_date_fiat", label: "Withdrawal fiat date", type: "count" },
												{ value: "withdrawal_count", label: "Withdrawal count", type: "count" },
												{ value: "withdrawal_count_fiat", label: "Withdrawal fiat count", type: "count" },
												{ value: "withdrawal_count_crypto", label: "Withdrawal crypto count", type: "count" },
												{ value: "withdrawal_from", label: "Withdrawal from", type: "date" },
												{ value: "withdrawal_to", label: "Withdrawal to", type: "date" },
												{ value: "withdrawal_crypto_from", label: "Withdrawal crypto from", type: "date" },
												{ value: "withdrawal_crypto_to", label: "Withdrawal crypto to", type: "date" },
												{ value: "withdrawal_fiat_from", label: "Withdrawal fiat from", type: "date" },
												{ value: "withdrawal_fiat_to", label: "Withdrawal fiat to", type: "date" },
												{ value: "withdrawal_amount_to", label: "Withdrawal amount to", type: "count" },
												{ value: "withdrawal_amount_from", label: "Withdrawal amount from", type: "count" },
												{ value: "withdrawal_amount_crypto_to", label: "Withdrawal amount crypto to", type: "count" },
												{ value: "withdrawal_amount_crypto_from", label: "Withdrawal amount crypto from", type: "count" },
												{ value: "withdrawal_amount_fiat_to", label: "Withdrawal amount fiat to", type: "count" },
												{ value: "withdrawal_amount_fiat_from", label: "Withdrawal amount fiat from", type: "count" },
												{ value: "withdrawal_type", label: "Withdrawal type", type: "select", specialType: "cryptoOrFiat" },
												{ value: "spins", label: "Spins", type: "count" },
												{ value: "lvl_club", label: "Club", type: "count" },
												{ value: "referer", label: "Referer", type: "count" },
												{ value: "balance", label: "Balance", type: "count" },
												{ value: "registration_from", label: "Registration from", type: "date" },
												{ value: "registration_to", label: "Registration to", type: "date" },
												{ value: "registration_date", label: "Registration user days", type: "count" },
											]}
											onChange={({ detail }) =>
												setNewConditions({
													...newCondition,
													field: detail.selectedOption.value,
													fieldLabel: detail.selectedOption.label,
													conditionalType: detail.selectedOption.type,
													specialType: detail.selectedOption.specialType,
													value: null,
													operator: detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect" ? "=" : null,
												})
											}
										/>

										<Select
											selectedOption={newCondition?.operator ? { value: newCondition?.operator, label: newCondition?.operator } : null}
											placeholder="Select operator"
											disabled={newCondition?.conditionalType === "select" || newCondition?.conditionalType === "multiselect"}
											options={cohortOperatorOptions}
											onChange={({ detail }) =>
												setNewConditions({
													...newCondition,
													operator: detail.selectedOption.value,
												})
											}
										/>


										{renderComponentFromType(newCondition?.conditionalType, newCondition?.specialType)}


										<Button
											disabled={typeof newCondition?.operator === "undefined" || isConditionValueEmpty(newCondition?.value) || newCondition?.field === null}
											onClick={() => {
												if (!newCondition?.field || isConditionValueEmpty(newCondition?.value)) return;
												const conditionToAdd = { ...newCondition };
												if (conditionToAdd.field === "lang") {
													conditionToAdd.value = formatLanguageCohortValue(conditionToAdd.value, conditionToAdd.operator);
													if (isConditionValueEmpty(conditionToAdd.value)) return;
												}
												const newConds = [
													...(cohort || []),
													conditionToAdd,
												].map((element) => {
													let value = element.value;
													if (element.conditionalType === "select") {
														value = element.value.value || element.value;
													} else if (element.conditionalType === "boolean") {
														value = element.value ? 1 : 0;
													}

													return { ...element, value, id: crypto.randomUUID() };
												});

												updateFormField("conditions", newConds);
												setNewConditions({});
											}}
										>
											+ Add
										</Button>
									</Grid>
								</FormField>

							</>}
							items={(cohort || [])}
							empty="No conditions set"
						/>
					</>
				</SpaceBetween>
			</Container>
		);
	};

	const renderButtonTab = () => {
		const formData = store.formData;

		React.useEffect(() => {
			const hasButtonData = formData.buttonType || formData.buttonLink || formData.buttonText || formData.buttonLinkType || formData.buttonTranslationType;

			if (hasButtonData && !buttonEnabled) {
				setButtonEnabled(true);
			}
		}, [formData.buttonType, formData.buttonLink, formData.buttonText, formData.buttonLinkType, formData.buttonTranslationType, buttonEnabled]);

		return (
			<Container>
				<SpaceBetween size="m">
					<Box variant="h3">Button Configuration</Box>

					<FormField label="">
						<Toggle
							checked={buttonEnabled}
							onChange={(e) => {
								if (!e.detail.checked) {
									setStore((prev) => ({ ...prev, formData: { ...prev.formData, buttonType: "", buttonLink: "", buttonText: "", buttonLinkType: "", buttonTranslationType: "" } }));

									const newFieldErrors = { ...fieldErrors };
									["buttonType", "buttonLink", "buttonText", "buttonLinkType", "buttonTranslationType"].forEach((field) => {
										delete newFieldErrors[field];
									});
									setFieldErrors(newFieldErrors);
								}

								setTimeout(() => {
									setButtonEnabled(e.detail.checked);
								}, 0);
							}}>
							Enable Button
						</Toggle>
					</FormField>

					{buttonEnabled ? (
						<>
							<Box variant="awsui-key-label">When enabled, all button fields are required</Box>

							<ColumnLayout columns={2}>
								<FormField label="Button Design Type *" errorText={fieldErrors.buttonType ? "This field is required" : ""}>
									<Select
										selectedOption={formData.buttonType ? { value: formData.buttonType, label: formData.buttonType.charAt(0).toUpperCase() + formData.buttonType.slice(1) } : null}
										onChange={(e) => {
											updateFormField("buttonType", e.detail.selectedOption ? e.detail.selectedOption.value : "");
											clearFieldError("buttonType");
										}}
										options={[
											{ value: "primary", label: "Primary" },
											{ value: "transparent", label: "Transparent" },
											{ value: "secondary", label: "Secondary" },
											{ value: "white", label: "White" },
											{ value: "danger", label: "Danger" },
											{ value: "dark", label: "Dark" },
											{ value: "info", label: "Info" },
										]}
										placeholder="Select button design"
										invalid={fieldErrors.buttonType}
									/>
								</FormField>

								<FormField label="Button Text *" errorText={fieldErrors.buttonText ? "This field is required" : ""}>
									<Input
										value={formData.buttonText || ""}
										onChange={(e) => {
											updateFormField("buttonText", e.detail.value);
											clearFieldError("buttonText");
										}}
										placeholder="Click me"
										invalid={fieldErrors.buttonText}
									/>
								</FormField>

								<FormField label="Button Link *" errorText={fieldErrors.buttonLink ? "This field is required" : ""}>
									<Input
										value={formData.buttonLink || ""}
										onChange={(e) => {
											updateFormField("buttonLink", e.detail.value);
											clearFieldError("buttonLink");
										}}
										placeholder="https://example.com"
										invalid={fieldErrors.buttonLink}
									/>
								</FormField>

								<FormField label="Button Link Type *" errorText={fieldErrors.buttonLinkType ? "This field is required" : ""}>
									<Select
										selectedOption={formData.buttonLinkType ? { value: formData.buttonLinkType, label: formData.buttonLinkType.charAt(0).toUpperCase() + formData.buttonLinkType.slice(1) } : null}
										onChange={(e) => {
											updateFormField("buttonLinkType", e.detail.selectedOption ? e.detail.selectedOption.value : "");
											clearFieldError("buttonLinkType");
										}}
										options={[
											{ value: "internal", label: "Internal" },
											{ value: "external", label: "External" },
											{ value: "modal", label: "Modal" },
										]}
										placeholder="Select button link type"
										invalid={fieldErrors.buttonLinkType}
									/>
								</FormField>

								<FormField label="Button Translation Type *" errorText={fieldErrors.buttonTranslationType ? "This field is required" : ""}>
									<Select
										selectedOption={formData.buttonTranslationType ? { value: formData.buttonTranslationType, label: formData.buttonTranslationType.charAt(0).toUpperCase() + formData.buttonTranslationType.slice(1) } : null}
										onChange={(e) => {
											updateFormField("buttonTranslationType", e.detail.selectedOption ? e.detail.selectedOption.value : "");
											clearFieldError("buttonTranslationType");
										}}
										options={[
											{ value: "local", label: "Local" },
											{ value: "international", label: "International" },
											{ value: "mixed", label: "Mixed" },
										]}
										placeholder="Select button translation type"
										invalid={fieldErrors.buttonTranslationType}
									/>
								</FormField>
							</ColumnLayout>
						</>
					) : (
						<Box variant="p" color="text-body-secondary">
							Enable button to add a call-to-action button to your notification. When enabled, all fields become required.
						</Box>
					)}
				</SpaceBetween>
			</Container>
		);
	};

	const renderShowSettingsTab = () => {
		const formData = store.formData;
		const showSettings = formData.showSettings && formData.showSettings[0] ? formData.showSettings[0] : { page: "", seconds: 5 };

		return (
			<Container>
				<SpaceBetween size="m">
					<Box variant="p">Configure where and when the notification should be displayed</Box>

					<ColumnLayout columns={2}>
						<FormField label="Page *" description="Where to show the notification" errorText={fieldErrors.page ? "This field is required" : ""}>
							<Select
								selectedOption={showSettings.page ? { value: showSettings.page, label: showSettings.page.charAt(0).toUpperCase() + showSettings.page.slice(1) } : null}
								onChange={(e) => {
									updateShowSetting(0, "page", e.detail.selectedOption ? e.detail.selectedOption.value : "");
									clearFieldError("page");
								}}
								options={[
									{ value: "any", label: "Any Page" },
									{ value: "home", label: "Home Page" },
									{ value: "vip", label: "VIP Page" },
									{ value: "wallet", label: "Wallet" },
									{ value: "promocode", label: "Promocode Window" },
									{ value: "security", label: "Security Settings" },
									{ value: "verification", label: "Verification Settings" },
								]}
								placeholder="Select page"
								invalid={fieldErrors.page}
							/>
						</FormField>

						<FormField label="Display Delay (seconds) *" description="Show notification after X seconds on the page" errorText={fieldErrors.seconds ? "This field is required" : ""}>
							<Input
								type="number"
								value={showSettings.seconds !== undefined && showSettings.seconds !== null ? showSettings.seconds : ""}
								onChange={(e) => {
									const value = e.detail.value;
									// Parse as integer, or keep as empty string
									const newValue = value === "" ? "" : parseInt(value) || 0;
									updateShowSetting(0, "seconds", newValue);
									clearFieldError("seconds");
								}}
								placeholder="Enter seconds"
								invalid={fieldErrors.seconds}
							/>
						</FormField>
					</ColumnLayout>
				</SpaceBetween>
			</Container>
		);
	};

	const renderJsonTab = () => {
		return (
			<Container>
				<SpaceBetween size="m">
					<Box variant="p" color="text-body-secondary">
						Copy the current notification as JSON or paste JSON and apply it to update the form fields.
					</Box>
					<SpaceBetween direction="horizontal" size="xs">
						<Button onClick={copyNotificationJson}>Copy JSON</Button>
						<Button variant="primary" onClick={applyJsonToForm}>
							Apply JSON
						</Button>
					</SpaceBetween>
					<FormField label="JSON Editor" errorText={jsonError} stretch>
						<Textarea
							value={jsonText}
							onChange={({ detail }) => {
								setJsonText(detail.value);
								setJsonError(getJsonValidationError(detail.value));
							}}
							rows={24}
						/>
					</FormField>
				</SpaceBetween>
			</Container>
		);
	};

	if (!store.show) return null;

	const tabs = [
		{
			id: "general",
			label: "General",
			content: renderGeneralTab(),
		},
		{
			id: "button",
			label: "Button",
			content: renderButtonTab(),
		},
		{
			id: "cohort",
			label: "Cohort Settings",
			content: renderCohortTab(),
		},
		{
			id: "show-settings",
			label: "Show Settings",
			content: renderShowSettingsTab(),
		},
		{
			id: "json",
			label: "JSON",
			content: renderJsonTab(),
		},
	];

	const modalTitle = mode === "edit" ? `Edit Notification #${store.editId}` : "Add Notification";
	const saveButtonText = mode === "edit" ? "Save Changes" : "Create Notification";

	return (
		<Modal
			visible={store.show}
			onDismiss={handleClose}
			header={modalTitle}
			size="large"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="link" onClick={handleClose}>
							Cancel
						</Button>
						{mode === "edit" && (
							<Button variant="normal" onClick={cloneNotification}>
								Clone as New
							</Button>
						)}
						<Button variant="primary" loading={isLoading} disabled={isLoading} onClick={handleSave}>
							{saveButtonText}
						</Button>
					</SpaceBetween>
				</Box>
			}>
			<Tabs tabs={tabs} activeTabId={activeTab} onChange={(e) => setActiveTab(e.detail.activeTabId)} />
		</Modal>
	);
}

export default NotificationModal;
